// BackgroundLayer: a full-viewport dark-navy -> electric-blue backdrop with a
// subtle vignette and a sparse field of slowly drifting glow particles. Drawn
// with Graphics (no assets). On phones (reducedEffects) the particle field is
// dropped to save fill-rate. It owns the whole canvas area so there is never a
// flat letterbox band around the composed UI.
import { Container, Graphics } from 'pixi.js';

interface Particle {
  x: number;
  y: number;
  r: number;
  speed: number;
  phase: number;
}

export class BackgroundLayer extends Container {
  private readonly base = new Graphics();
  private readonly particlesGfx = new Graphics();
  private particles: Particle[] = [];
  private w = 1;
  private h = 1;
  private reduced = false;
  private t = 0;

  constructor() {
    super();
    this.addChild(this.base, this.particlesGfx);
  }

  setReducedEffects(reduced: boolean): void {
    this.reduced = reduced;
    if (reduced) {
      this.particles = [];
      this.particlesGfx.clear();
    } else if (this.particles.length === 0 && this.w > 1) {
      this.seedParticles();
    }
  }

  layout(width: number, height: number): void {
    this.w = Math.max(1, width);
    this.h = Math.max(1, height);
    this.drawBase();
    if (!this.reduced) this.seedParticles();
  }

  /** Advances the drifting particles. No-op when reduced-motion is active. */
  tickIdle(dtMs: number): void {
    if (this.reduced || this.particles.length === 0) return;
    this.t += dtMs;
    const g = this.particlesGfx;
    g.clear();
    for (const p of this.particles) {
      p.y -= p.speed * (dtMs / 1000);
      if (p.y < -10) p.y = this.h + 10;
      const twinkle = 0.4 + 0.35 * Math.sin(this.t / 700 + p.phase);
      g.circle(p.x, p.y, p.r).fill({ color: 0x6ee2ff, alpha: 0.18 * twinkle });
    }
  }

  private drawBase(): void {
    const g = this.base;
    g.clear();
    // Vertical bands fake a smooth navy gradient (cheap, no shader).
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      const f = i / (bands - 1);
      const color = lerpColor(0x0a1330, 0x0f2a63, f);
      g.rect(0, (this.h * i) / bands, this.w, this.h / bands + 1).fill({ color });
    }
    // Electric-blue radial glow near the top center.
    g.ellipse(this.w / 2, this.h * 0.16, this.w * 0.5, this.h * 0.3).fill({ color: 0x1f6dff, alpha: 0.18 });
    // Corner vignette.
    g.rect(0, 0, this.w, this.h).stroke({ width: this.w * 0.12, color: 0x05070f, alpha: 0.5 });
  }

  private seedParticles(): void {
    const count = Math.min(48, Math.max(14, Math.round((this.w * this.h) / 26000)));
    this.particles = Array.from({ length: count }, (_, i) => ({
      x: pseudo(i * 3 + 1) * this.w,
      y: pseudo(i * 3 + 2) * this.h,
      r: 1 + pseudo(i * 3 + 3) * 2.4,
      speed: 6 + pseudo(i * 7 + 1) * 16,
      phase: pseudo(i * 11 + 5) * Math.PI * 2,
    }));
  }
}

function pseudo(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
